import React from "react";
import { Link } from "react-router-dom";
import { PublicLayout, Eyebrow, SectionCTA, IMG, ClientServicesPreview, ReferralPreview } from "@/components/public/PublicKit";
import {
  ArrowRight, Users2, ClipboardCheck, Layers, Share2, Eye,
  Building2, HeartPulse, Pill, Syringe, CheckCircle2,
} from "lucide-react";

const CONNECTS = [
  { icon: Users2, title: "Provider CRM", desc: "Leads, practices, contacts, account owners, opportunities, tasks, and follow-ups in one shared pipeline." },
  { icon: ClipboardCheck, title: "Client onboarding", desc: "Track documentation, DTCM form completion, and readiness to start — so nothing stalls between sold and active." },
  { icon: Layers, title: "Multiple services", desc: "A client can run several services at once — 340B, RPM, and infusion each progress on their own track." },
  { icon: Share2, title: "Referral tracking", desc: "Follow every referral from submission through completion, with owners, follow-up dates, and status history." },
  { icon: Eye, title: "Team visibility", desc: "See responsible owners, recent activity, and the next action on any client or referral at a glance." },
];

const SOLUTIONS = [
  { icon: Building2, title: "MSOs", desc: "Coordinate provider acquisition and multi-service onboarding across your managed practices." },
  { icon: HeartPulse, title: "RPM providers", desc: "Move practices from interested to enrolled and keep remote monitoring activations on track." },
  { icon: Pill, title: "Specialty pharmacy & 340B", desc: "Track prescription activation and 340B service milestones per client and partner." },
  { icon: Syringe, title: "Infusion providers", desc: "Follow infusion referrals and know exactly when a client starts sending." },
];

export default function Landing() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-emerald-100/60 blur-3xl" />
        <div className="pointer-events-none absolute -left-40 top-40 h-80 w-80 rounded-full bg-teal-100/50 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-[1.1fr_1fr] lg:px-8 lg:py-24">
          <div>
            <Eyebrow>CRM for 340B &amp; RPM with Referral Tracking</Eyebrow>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              The CRM that connects provider relationships to service activation.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-500">
              Manage physician relationships, onboard clients, and track specialty pharmacy 340B, RPM, and infusion
              referrals—all in one place.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/book-demo" data-testid="hero-book-demo" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5 active:scale-95">
                Book a Demo <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/platform" data-testid="hero-explore" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-base font-bold text-slate-800 hover:border-primary hover:text-primary">
                Explore the Platform
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-slate-500">
              {["MSOs", "RPM providers", "Specialty pharmacy & 340B", "Infusion providers"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={IMG.hero} alt="Clinician reviewing remote patient data" loading="eager"
              className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl ring-1 ring-slate-200" />
            <div className="absolute -bottom-6 -left-6 hidden w-64 sm:block">
              <ReferralPreview />
            </div>
          </div>
        </div>
      </section>

      {/* What HudlUp connects */}
      <section className="border-t border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <div className="max-w-2xl">
            <Eyebrow>One connected workspace</Eyebrow>
            <h2 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl">From first conversation to active service</h2>
            <p className="mt-3 text-lg text-slate-500">HudlUp connects provider acquisition, client onboarding, service activation, and referral follow-up — so your team always knows the next action.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CONNECTS.map((f) => (
              <div key={f.title} data-testid={`connect-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md">
                <div className="mb-3 inline-flex rounded-xl bg-primary/10 p-3 text-primary"><f.icon className="h-6 w-6" /></div>
                <p className="font-display text-lg font-bold text-slate-900">{f.title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Previews */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <Eyebrow>Clients &amp; Services</Eyebrow>
            <h2 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl">Every service on its own track</h2>
            <p className="mt-3 text-lg text-slate-500">One client can have DTCM completed, RPM started with a partner, and infusion still onboarding — with clear statuses and owners for each.</p>
            <ul className="mt-5 space-y-2.5">
              {["Clearly labeled client status for converted clients", "Multiple services per client, each progressing independently", "Assigned partner, start date, and responsible owner per service"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-slate-600"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
          <ClientServicesPreview />
        </div>
      </section>

      {/* Solutions */}
      <section className="border-t border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <div className="max-w-2xl">
            <Eyebrow>Built for your model</Eyebrow>
            <h2 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl">Solutions for every service organization</h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SOLUTIONS.map((s) => (
              <div key={s.title} data-testid={`solution-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 inline-flex rounded-xl bg-primary/10 p-3 text-primary"><s.icon className="h-6 w-6" /></div>
                <p className="font-display text-lg font-bold text-slate-900">{s.title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/solutions" className="inline-flex items-center gap-1.5 font-bold text-primary hover:underline">See all solutions <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <SectionCTA />
    </PublicLayout>
  );
}
